import React from "react";
import { Card, CardContent } from "../../../components/ui/card";
import { Progress } from "../../../components/ui/progress";
import { Badge } from "../../../components/ui/badge";
import { Cpu, MemoryStick, HardDrive, Activity } from "lucide-react";

/**
 * Live OS / hardware / runtime panel for the Super Admin → System page.
 * Receives the raw payload from `GET /api/admin/system-info` and renders it
 * across multiple grouped cards. Each card tries to surface 100% of the
 * data the backend collected so admins can copy-paste straight to support.
 */

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0 || bytes == null) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.min(Math.floor(Math.log(Math.abs(bytes)) / Math.log(k)), sizes.length - 1);
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};

const usageTone = (pct) => {
  if (pct >= 90) return "text-red-500";
  if (pct >= 75) return "text-amber-500";
  if (pct >= 50) return "text-yellow-500";
  return "text-emerald-500";
};

const usageBg = (pct) => {
  if (pct >= 90) return "bg-red-500";
  if (pct >= 75) return "bg-amber-500";
  if (pct >= 50) return "bg-yellow-500";
  return "bg-emerald-500";
};

const KV = ({ label, value, mono = false, "data-testid": testId }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-border/40 last:border-b-0" data-testid={testId}>
    <span className="text-xs text-muted-foreground whitespace-nowrap">{label}</span>
    <span className={`text-xs text-foreground text-right break-all ${mono ? "font-mono" : ""}`}>
      {value == null || value === "" ? <span className="text-muted-foreground/50">—</span> : value}
    </span>
  </div>
);

const SectionCard = ({ icon: Icon, iconClass, title, accent, children, testId }) => (
  <Card className="glass-card border-theme overflow-hidden" data-testid={testId}>
    <div className={`px-4 py-2.5 border-b border-border/50 flex items-center gap-2 ${accent || "bg-muted/30"}`}>
      {Icon && <Icon className={`w-4 h-4 ${iconClass || "text-foreground"}`} />}
      <h3 className="text-sm font-semibold text-foreground font-['Manrope']">{title}</h3>
    </div>
    <CardContent className="p-4 space-y-0.5">{children}</CardContent>
  </Card>
);

const UsageBar = ({ label, percent, used, total, sub, testId }) => (
  <div className="space-y-1.5" data-testid={testId}>
    <div className="flex justify-between items-baseline">
      <span className="text-xs font-medium text-foreground">{label}</span>
      <span className={`text-xs font-bold tabular-nums ${usageTone(percent)}`}>{percent?.toFixed?.(1) ?? percent}%</span>
    </div>
    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
      <div
        className={`h-full transition-all ${usageBg(percent)}`}
        style={{ width: `${Math.min(100, Math.max(0, percent || 0))}%` }}
      />
    </div>
    {(used != null || sub) && (
      <div className="text-[10px] text-muted-foreground tabular-nums">
        {used != null && total != null && <span>{formatBytes(used)} / {formatBytes(total)}</span>}
        {sub && <span className="ml-1">{sub}</span>}
      </div>
    )}
  </div>
);

const ServerInfoPanel = ({ systemInfo }) => {
  if (!systemInfo) {
    return (
      <Card className="glass-card border-theme">
        <CardContent className="p-12 text-center text-muted-foreground text-sm">
          Loading server snapshot…
        </CardContent>
      </Card>
    );
  }
  const { host, cpu, memory, swap, disks, app_disk, network, process: proc, runtime, container } = systemInfo;
  // Show "Container limit" badge when memory numbers come from cgroups —
  // it's how an admin verifies the displayed numbers are container-accurate
  // (e.g., 8 GB pod limit) rather than the underlying VM (e.g., 31 GB host).
  const memSourceLabel =
    memory.source === "cgroup_v2" ? "Container limit (cgroup v2)" :
    memory.source === "cgroup_v1" ? "Container limit (cgroup v1)" :
    "Host (no container limit)";
  const isContainerLimited = memory.source && memory.source.startsWith("cgroup");

  return (
    <div className="space-y-4" data-testid="server-info-panel">
      {/* Quick-read usage bars row — what most admins want at a glance */}
      <Card className="glass-card border-theme">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            <div data-testid="cpu-usage-block">
              <div className="flex items-center gap-1.5 mb-2">
                <Cpu className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-xs font-semibold text-foreground">CPU</span>
                {cpu.percent_source?.startsWith?.("cgroup") && (
                  <Badge variant="outline" className="text-[9px] border-emerald-500/30 text-emerald-500" title="Measured from this container's cgroup (cpu.stat), not the shared node">
                    container
                  </Badge>
                )}
                <span className="text-[10px] text-muted-foreground ml-auto">
                  {cpu.logical_cores} cores{cpu.cores_in_use != null ? ` · using ${cpu.cores_in_use}` : ""}
                </span>
              </div>
              <UsageBar
                label=""
                percent={cpu.percent_total}
                sub={cpu.cgroup_quota_cores ? `of ${cpu.cgroup_quota_cores}-core quota` : (cpu.freq_current_mhz ? `${cpu.freq_current_mhz} MHz` : null)}
              />
            </div>
            <div data-testid="memory-usage-block">
              <div className="flex items-center gap-1.5 mb-2">
                <MemoryStick className="w-3.5 h-3.5 text-purple-500" />
                <span className="text-xs font-semibold text-foreground">Memory</span>
                {isContainerLimited && (
                  <Badge variant="outline" className="ml-auto text-[9px] border-emerald-500/30 text-emerald-500" title={memSourceLabel}>
                    container
                  </Badge>
                )}
              </div>
              <UsageBar
                label=""
                percent={memory.percent}
                used={memory.used}
                total={memory.total}
              />
            </div>
            <div data-testid="disk-usage-block">
              <div className="flex items-center gap-1.5 mb-2">
                <HardDrive className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-xs font-semibold text-foreground">Disk (/app)</span>
              </div>
              <UsageBar
                label=""
                percent={app_disk?.percent ?? 0}
                used={app_disk?.used}
                total={app_disk?.total}
              />
            </div>
            <div data-testid="load-avg-block">
              <div className="flex items-center gap-1.5 mb-2">
                <Activity className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-xs font-semibold text-foreground">Node Load</span>
                <Badge variant="outline" className="ml-auto text-[9px] border-amber-500/30 text-amber-500" title={cpu.host_shared?.note || "Shared-node figure — includes all pods on this node"}>
                  shared node
                </Badge>
              </div>
              <div className="flex justify-around text-center">
                <div>
                  <div className={`text-base font-bold tabular-nums ${usageTone(((cpu.host_shared?.load_avg_1m ?? 0) / (cpu.host_logical_cores || 1)) * 100)}`}>{cpu.host_shared?.load_avg_1m ?? "—"}</div>
                  <div className="text-[10px] text-muted-foreground">1 min</div>
                </div>
                <div>
                  <div className="text-base font-bold tabular-nums text-foreground">{cpu.host_shared?.load_avg_5m ?? "—"}</div>
                  <div className="text-[10px] text-muted-foreground">5 min</div>
                </div>
                <div>
                  <div className="text-base font-bold tabular-nums text-foreground">{cpu.host_shared?.load_avg_15m ?? "—"}</div>
                  <div className="text-[10px] text-muted-foreground">15 min</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Container Resources — explicit cgroup limits */}
      {container && container.source && container.source !== "none" && (
        <Card className="glass-card border-emerald-500/30 bg-emerald-500/5" data-testid="container-resources-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-500">
                {container.source.replace("_", " ")}
              </Badge>
              <h3 className="text-sm font-semibold text-foreground font-['Manrope']">Container Resource Limits</h3>
              <span className="text-[10px] text-muted-foreground ml-auto">
                These are your pod&#39;s actual limits — what the OS will enforce.
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-background/50 border border-border/50 rounded-lg p-3" data-testid="container-mem-limit">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Memory limit</div>
                <div className="text-lg font-bold text-foreground tabular-nums">
                  {container.memory_limit_bytes ? formatBytes(container.memory_limit_bytes) : "unlimited"}
                </div>
                <div className="text-[10px] text-muted-foreground tabular-nums">
                  used {container.memory_current_bytes ? formatBytes(container.memory_current_bytes) : "—"}
                  {container.memory_peak_bytes ? ` · peak ${formatBytes(container.memory_peak_bytes)}` : ""}
                </div>
              </div>
              <div className="bg-background/50 border border-border/50 rounded-lg p-3" data-testid="container-cpu-limit">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">CPU quota</div>
                <div className="text-lg font-bold text-foreground tabular-nums">
                  {container.cpu_quota_cores != null ? `${container.cpu_quota_cores} cores` : "unlimited"}
                </div>
                <div className="text-[10px] text-muted-foreground tabular-nums">
                  {container.cpu_quota_us != null && container.cpu_period_us != null
                    ? `${container.cpu_quota_us} / ${container.cpu_period_us} µs`
                    : "no quota set"}
                </div>
              </div>
              <div className="bg-background/50 border border-border/50 rounded-lg p-3" data-testid="container-swap-limit">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Swap limit</div>
                <div className="text-lg font-bold text-foreground tabular-nums">
                  {container.swap_limit_bytes != null
                    ? (container.swap_limit_bytes === 0 ? "disabled" : formatBytes(container.swap_limit_bytes))
                    : "unlimited"}
                </div>
                <div className="text-[10px] text-muted-foreground tabular-nums">
                  used {container.swap_current_bytes != null ? formatBytes(container.swap_current_bytes) : "—"}
                </div>
              </div>
              <div className="bg-background/50 border border-border/50 rounded-lg p-3" data-testid="container-disk-limit">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">/app volume</div>
                <div className="text-lg font-bold text-foreground tabular-nums">
                  {app_disk?.total ? formatBytes(app_disk.total) : "—"}
                </div>
                <div className="text-[10px] text-muted-foreground tabular-nums">
                  used {app_disk?.used ? formatBytes(app_disk.used) : "—"}
                  {app_disk?.percent != null ? ` (${app_disk.percent}%)` : ""}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Host */}
        <SectionCard title="Host" testId="host-card" accent="bg-blue-500/10">
          <KV label="Hostname" value={host.hostname} mono testid="host-hostname" />
          <KV label="FQDN" value={host.fqdn} mono />
          <KV label="System" value={`${host.system} ${host.release}`} />
          <KV label="Architecture" value={host.machine} mono />
          <KV label="Platform" value={host.platform} mono />
          <KV label="In Container" value={host.in_container ? "Yes" : "No"} />
          <KV label="Boot Time" value={new Date(host.boot_time).toLocaleString()} />
          <KV label="Kernel Uptime" value={host.kernel_uptime_human} mono />
          <KV label="Server Time" value={new Date(host.current_time_utc).toLocaleString()} />
          <KV label="Timezone" value={host.timezone} mono />
        </SectionCard>

        {/* CPU */}
        <SectionCard title="CPU" testId="cpu-card" accent="bg-purple-500/10">
          <KV label="Model" value={cpu.model} mono />
          <KV label="Effective cores" value={cpu.logical_cores} />
          {cpu.cgroup_quota_cores != null && (
            <KV label="Container quota" value={`${cpu.cgroup_quota_cores} cores (${cpu.cgroup_quota_us}/${cpu.cgroup_period_us} µs)`} mono />
          )}
          {cpu.host_logical_cores != null && cpu.host_logical_cores !== cpu.logical_cores && (
            <KV label="Host logical cores" value={cpu.host_logical_cores} />
          )}
          <KV label="Physical Cores" value={cpu.physical_cores} />
          <KV
            label="Container CPU"
            value={cpu.percent_total != null
              ? `${cpu.percent_total?.toFixed?.(1) ?? cpu.percent_total}%${cpu.cgroup_quota_cores ? " of quota" : ""}${cpu.cores_in_use != null ? ` · ${cpu.cores_in_use} cores in use` : ""}`
              : null}
            mono
          />
          {cpu.throttling?.nr_periods != null && (
            <KV
              label="CPU Throttling (cgroup)"
              value={`${cpu.throttling.nr_throttled ?? 0} / ${cpu.throttling.nr_periods} periods${cpu.throttling.throttled_pct != null ? ` (${cpu.throttling.throttled_pct}%)` : ""}`}
              mono
            />
          )}
          <KV
            label="Frequency"
            value={cpu.freq_current_mhz ? `${cpu.freq_current_mhz} / ${cpu.freq_max_mhz || "?"} MHz` : null}
            mono
          />
          <KV label="Node CPU (shared)" value={cpu.host_shared?.percent_total != null ? `${cpu.host_shared.percent_total?.toFixed?.(1) ?? cpu.host_shared.percent_total}%` : null} mono />
          <KV label="Node load 1/5/15m (shared)" value={cpu.host_shared ? `${cpu.host_shared.load_avg_1m} / ${cpu.host_shared.load_avg_5m} / ${cpu.host_shared.load_avg_15m}` : null} mono />
          {Array.isArray(cpu.host_shared?.percent_per_core) && cpu.host_shared.percent_per_core.length > 0 && (
            <div className="pt-2 mt-1 border-t border-border/40">
              <div className="text-[10px] text-muted-foreground mb-1.5">Per-core usage — shared node (all pods, context only)</div>
              <div className="grid grid-cols-4 gap-1.5">
                {cpu.host_shared.percent_per_core.map((pct, i) => (
                  <div key={i} className="bg-muted/40 rounded p-1.5 text-center" data-testid={`cpu-core-${i}`}>
                    <div className={`text-[10px] font-bold tabular-nums ${usageTone(pct)}`}>{pct.toFixed(0)}%</div>
                    <div className="text-[9px] text-muted-foreground">CPU{i}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionCard>

        {/* Memory */}
        <SectionCard title="Memory" testId="memory-card" accent="bg-emerald-500/10">
          <KV label="Source" value={memSourceLabel} />
          <KV label="Total" value={formatBytes(memory.total)} mono />
          <KV label="Used" value={`${formatBytes(memory.used)} (${memory.percent}%)`} mono />
          <KV label="Available" value={formatBytes(memory.available)} mono />
          <KV label="Free" value={formatBytes(memory.free)} mono />
          <KV label="Buffers" value={formatBytes(memory.buffers)} mono />
          <KV label="Cached" value={formatBytes(memory.cached)} mono />
          {memory.cgroup_peak_bytes != null && (
            <KV label="Peak (since boot)" value={formatBytes(memory.cgroup_peak_bytes)} mono />
          )}
          {isContainerLimited && memory.host_total != null && (
            <>
              <div className="pt-2 mt-1 border-t border-border/40 text-[10px] text-muted-foreground mb-1">
                Underlying host (reference only, not your limit)
              </div>
              <KV label="Host total" value={formatBytes(memory.host_total)} mono />
              <KV label="Host used" value={formatBytes(memory.host_used)} mono />
              <KV label="Host available" value={formatBytes(memory.host_available)} mono />
            </>
          )}
          {swap && swap.total > 0 && (
            <>
              <div className="pt-2 mt-1 border-t border-border/40 text-[10px] text-muted-foreground mb-1">Swap</div>
              <KV label="Swap Total" value={formatBytes(swap.total)} mono />
              <KV label="Swap Used" value={`${formatBytes(swap.used)} (${swap.percent}%)`} mono />
            </>
          )}
        </SectionCard>

        {/* Process */}
        <SectionCard title="Backend Process" testId="process-card" accent="bg-amber-500/10">
          <KV label="PID" value={proc.pid} mono />
          <KV label="User" value={proc.username} mono />
          <KV label="Started" value={proc.create_time && new Date(proc.create_time).toLocaleString()} />
          <KV label="Uptime" value={proc.uptime_human} mono />
          <KV label="Threads" value={proc.num_threads} />
          <KV label="Open File Descriptors" value={proc.num_fds} />
          <KV label="Open Files" value={proc.open_files} />
          <KV label="Network Connections" value={proc.connections} />
          <KV label="CPU Usage" value={proc.cpu_percent != null ? `${proc.cpu_percent}%` : null} mono />
          <KV label="Memory RSS" value={formatBytes(proc.memory_rss_bytes)} mono />
          <KV label="Memory VMS" value={formatBytes(proc.memory_vms_bytes)} mono />
        </SectionCard>
      </div>

      {/* Disks */}
      <Card className="glass-card border-theme" data-testid="disks-card">
        <div className="px-4 py-2.5 border-b border-border/50 flex items-center gap-2 bg-emerald-500/10">
          <HardDrive className="w-4 h-4 text-emerald-500" />
          <h3 className="text-sm font-semibold text-foreground font-['Manrope']">Disks &amp; Filesystems</h3>
          <Badge variant="outline" className="ml-auto text-[10px]">{disks?.length || 0} mount{(disks?.length || 0) === 1 ? "" : "s"}</Badge>
        </div>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/30">
                <tr className="text-left text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Mount</th>
                  <th className="px-4 py-2 font-medium">Scope</th>
                  <th className="px-4 py-2 font-medium">Device</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium text-right">Used</th>
                  <th className="px-4 py-2 font-medium text-right">Total</th>
                  <th className="px-4 py-2 font-medium" style={{ width: "20%" }}>Usage</th>
                </tr>
              </thead>
              <tbody>
                {(disks || []).map((d, i) => (
                  <tr key={`${d.device}-${d.mountpoint}`} className="border-t border-border/40" data-testid={`disk-row-${i}`}>
                    <td className="px-4 py-2 font-mono text-foreground">{d.mountpoint}</td>
                    <td className="px-4 py-2">
                      {d.scope === "container" ? (
                        <Badge variant="outline" className="text-[9px] border-emerald-500/30 text-emerald-500" title="Your app's own volume (shutil.disk_usage on /app)">
                          app volume
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[9px] border-amber-500/30 text-amber-500" title="Belongs to the shared Kubernetes node — context only, not your quota">
                          shared node
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono text-muted-foreground">{d.device}</td>
                    <td className="px-4 py-2 font-mono text-muted-foreground">{d.fstype}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatBytes(d.used)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatBytes(d.total)}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <Progress value={d.percent} className="h-1.5 flex-1" />
                        <span className={`text-[10px] font-bold tabular-nums ${usageTone(d.percent)}`} style={{ minWidth: 36 }}>
                          {d.percent}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
                {(disks || []).length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">No disks reported.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Network */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SectionCard title="Network I/O" testId="network-io-card" accent="bg-cyan-500/10">
          <KV label="Bytes Received" value={formatBytes(network?.io?.bytes_recv)} mono />
          <KV label="Bytes Sent" value={formatBytes(network?.io?.bytes_sent)} mono />
          <KV label="Packets Received" value={network?.io?.packets_recv?.toLocaleString()} mono />
          <KV label="Packets Sent" value={network?.io?.packets_sent?.toLocaleString()} mono />
          <KV label="Receive Errors" value={network?.io?.errin} mono />
          <KV label="Send Errors" value={network?.io?.errout} mono />
          <KV label="Drop In" value={network?.io?.dropin} mono />
          <KV label="Drop Out" value={network?.io?.dropout} mono />
        </SectionCard>

        <SectionCard title="Runtime" testId="runtime-card" accent="bg-rose-500/10">
          <KV label="Python" value={`${runtime.python_version} (${runtime.python_implementation})`} mono />
          <KV label="FastAPI" value={runtime.fastapi_version} mono />
          <KV label="psutil" value={runtime.psutil_version} mono />
          <KV label="Process Started" value={new Date(runtime.process_start_time).toLocaleString()} />
          <KV label="Module Uptime" value={runtime.module_uptime_human} mono />
          <KV label="Working Dir" value={runtime.cwd} mono />
          <KV label="Executable" value={runtime.executable} mono />
        </SectionCard>
      </div>

      {/* Network interfaces */}
      <Card className="glass-card border-theme" data-testid="interfaces-card">
        <div className="px-4 py-2.5 border-b border-border/50 flex items-center gap-2 bg-cyan-500/10">
          <Activity className="w-4 h-4 text-cyan-500" />
          <h3 className="text-sm font-semibold text-foreground font-['Manrope']">Network Interfaces</h3>
          <Badge variant="outline" className="ml-auto text-[10px]">{network?.interfaces?.length || 0}</Badge>
        </div>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/30">
                <tr className="text-left text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Interface</th>
                  <th className="px-4 py-2 font-medium">IPv4</th>
                  <th className="px-4 py-2 font-medium">IPv6</th>
                  <th className="px-4 py-2 font-medium">MAC</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Speed</th>
                </tr>
              </thead>
              <tbody>
                {(network?.interfaces || []).map((iface, i) => (
                  <tr key={iface.name} className="border-t border-border/40" data-testid={`iface-row-${i}`}>
                    <td className="px-4 py-2 font-mono">{iface.name}</td>
                    <td className="px-4 py-2 font-mono text-muted-foreground">{iface.ipv4 || "—"}</td>
                    <td className="px-4 py-2 font-mono text-muted-foreground truncate max-w-[180px]" title={iface.ipv6}>{iface.ipv6 || "—"}</td>
                    <td className="px-4 py-2 font-mono text-muted-foreground">{iface.mac || "—"}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className={`text-[10px] ${iface.is_up ? "border-emerald-500/30 text-emerald-500" : "border-red-500/30 text-red-500"}`}>
                        {iface.is_up ? "UP" : "DOWN"}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 tabular-nums">{iface.speed_mbps ? `${iface.speed_mbps} Mbps` : "—"}</td>
                  </tr>
                ))}
                {(network?.interfaces || []).length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No interfaces reported.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ServerInfoPanel;
